import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/16/solid"
import { LockClosedIcon, XMarkIcon } from "@heroicons/react/24/solid"
import AlertDialog from "./AlertDialog"
import Button from "./Button"

type ContentProps = {
  onConfirm: () => void
  onCancel: () => void
  isVerifying: boolean
  isPasskey: boolean
  isTokenExpired?: boolean
}

export function WalletVerificationDialog({
  open,
  onConfirm,
  onCancel,
  isVerifying,
  isFailure,
  isSessionExpired = false,
  isPasskey = false,
  isConfirmingPubKey = false,
  isAddingPubKey = false,
  accountId,
}: {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  isVerifying: boolean
  isFailure: boolean
  isSessionExpired?: boolean
  isPasskey?: boolean
  isConfirmingPubKey?: boolean
  isAddingPubKey?: boolean
  accountId?: string | null
}) {
  const getContent = () => {
    if (isConfirmingPubKey || isAddingPubKey) {
      return (
        <AddPubKeyContent
          onConfirm={onConfirm}
          onCancel={onCancel}
          isAddingPubKey={isAddingPubKey}
          accountId={accountId}
        />
      )
    }
    if (isFailure) {
      return (
        <FailureContent
          onConfirm={onConfirm}
          onCancel={onCancel}
          isVerifying={isVerifying}
          isPasskey={isPasskey}
        />
      )
    }
    if (isSessionExpired) {
      return (
        <SessionExpiredContent
          onConfirm={onConfirm}
          onCancel={onCancel}
          isVerifying={isVerifying}
          isPasskey={isPasskey}
        />
      )
    }
    return (
      <DefaultContent
        onConfirm={onConfirm}
        onCancel={onCancel}
        isVerifying={isVerifying}
        isPasskey={isPasskey}
      />
    )
  }

  return <AlertDialog open={open}>{getContent()}</AlertDialog>
}

function DefaultContent({
  onConfirm,
  onCancel,
  isVerifying,
  isPasskey,
  isTokenExpired = false,
}: ContentProps) {
  const authLabel = isPasskey ? "Passkey" : "wallet"

  return (
    <>
      <div className="mt-4 flex flex-col items-center">
        <div className="flex size-13 items-center justify-center rounded-full bg-gray-900 dark:bg-green-500/10">
          <LockClosedIcon className="size-6 text-green-500" />
        </div>
        <AlertDialog.Title className="mt-5">
          Verify your {authLabel}
        </AlertDialog.Title>
        <AlertDialog.Description className="mt-2">
          {isTokenExpired
            ? `Your previous login session has expired. Please sign a verification message with your ${authLabel} to continue.`
            : `Sign a message to verify ownership of your ${authLabel} and unlock all features.`}
        </AlertDialog.Description>
      </div>

      <ul className="mt-5 space-y-3 rounded-3xl bg-gray-50 p-5 dark:bg-gray-800">
        {[
          "Secure transactions and payments",
          "Access to all features",
          "Protection of your assets",
        ].map((text) => (
          <li key={text} className="flex items-center gap-1.5">
            <CheckCircleIcon className="size-4 shrink-0 text-gray-600 dark:text-gray-200" />
            <span className="font-medium text-gray-600 text-sm dark:text-gray-200">
              {text}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-col gap-2">
        <Button
          size="xl"
          fullWidth
          onClick={onConfirm}
          loading={isVerifying}
          data-testid="verify-wallet-confirm"
        >
          {isVerifying ? "Verifying..." : `Verify ${authLabel}`}
        </Button>
        <Button
          size="xl"
          fullWidth
          variant="secondary"
          onClick={onCancel}
          data-testid="verify-wallet-cancel"
        >
          Cancel
        </Button>
      </div>

      <p className="mt-3 text-center font-medium text-gray-500 text-sm dark:text-gray-400">
        Canceling will sign you out
      </p>
    </>
  )
}

function FailureContent({
  onConfirm,
  onCancel,
  isVerifying,
  isPasskey,
}: ContentProps) {
  const failureReasons = isPasskey
    ? [
        "The authentication was cancelled or timed out",
        "Your Passkey may not be available on this device",
      ]
    : [
        "The signature was rejected or timed out",
        "Some wallets may be incompatible",
      ]

  return (
    <>
      <div className="absolute inset-x-0 top-0 h-32 bg-linear-to-b from-red-500/10 to-red-500/0 dark:from-red-500/5 dark:to-red-500/0" />

      <div className="relative mt-4 flex flex-col items-center text-center">
        <div className="flex size-13 items-center justify-center rounded-full bg-red-500/15 dark:bg-red-500/25">
          <XMarkIcon className="size-6 text-red-600 dark:text-red-400" />
        </div>
        <AlertDialog.Title className="mt-5">
          Verification failed
        </AlertDialog.Title>
        <AlertDialog.Description className="mt-2">
          {isPasskey
            ? "We couldn\u2019t verify your Passkey. This might happen if the authentication was cancelled."
            : "We couldn\u2019t verify your wallet. This might happen if you rejected the signature request."}
        </AlertDialog.Description>
      </div>

      <ul className="mt-5 space-y-3 rounded-3xl bg-gray-50 p-5 dark:bg-gray-800">
        {failureReasons.map((text) => (
          <li key={text} className="flex items-center gap-1.5">
            <XCircleIcon className="size-4 text-gray-600 dark:text-gray-200" />
            <span className="font-medium text-gray-600 text-sm dark:text-gray-200">
              {text}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-col gap-2">
        <Button
          size="xl"
          fullWidth
          onClick={onConfirm}
          loading={isVerifying}
          data-testid="verify-wallet-retry"
        >
          Try again
        </Button>
        <Button
          size="xl"
          fullWidth
          variant="secondary"
          onClick={onCancel}
          data-testid="verify-wallet-signout"
        >
          Sign out
        </Button>
      </div>
    </>
  )
}

function SessionExpiredContent({
  onConfirm,
  onCancel,
  isVerifying,
  isPasskey,
}: ContentProps) {
  const authLabel = isPasskey ? "Passkey" : "wallet"

  return (
    <>
      <div className="mt-4 flex flex-col items-center text-center">
        <div className="flex size-13 items-center justify-center rounded-full bg-gray-900 dark:bg-green-500/10">
          <LockClosedIcon className="size-6 text-green-500" />
        </div>
        <AlertDialog.Title className="mt-5">Session expired</AlertDialog.Title>
        <AlertDialog.Description className="mt-2">
          Your session has expired. Please verify your {authLabel} again to
          continue using all features.
        </AlertDialog.Description>
      </div>

      <ul className="mt-5 space-y-3 rounded-3xl bg-gray-50 p-5 dark:bg-gray-800">
        {[
          "Secure transactions and payments",
          "Access to all features",
          "Protection of your assets",
        ].map((text) => (
          <li key={text} className="flex items-center gap-1.5">
            <CheckCircleIcon className="size-4 shrink-0 text-gray-600 dark:text-gray-200" />
            <span className="font-medium text-gray-600 text-sm dark:text-gray-200">
              {text}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-col gap-2">
        <Button
          size="xl"
          fullWidth
          onClick={onConfirm}
          loading={isVerifying}
          data-testid="verify-wallet-confirm"
        >
          {isVerifying ? "Verifying..." : "Verify again"}
        </Button>
        <Button
          size="xl"
          fullWidth
          variant="secondary"
          onClick={onCancel}
          data-testid="verify-wallet-cancel"
        >
          Cancel
        </Button>
      </div>

      <p className="mt-3 text-center font-medium text-gray-500 text-sm dark:text-gray-400">
        Canceling will sign you out
      </p>
    </>
  )
}

function AddPubKeyContent({
  onConfirm,
  onCancel,
  isAddingPubKey,
  accountId,
}: {
  onConfirm: () => void
  onCancel: () => void
  isAddingPubKey: boolean
  accountId?: string | null
}) {
  return (
    <>
      <div className="mt-4 flex flex-col items-center">
        <div className="flex size-13 items-center justify-center rounded-full bg-gray-900 dark:bg-green-500/10">
          <LockClosedIcon className="size-6 text-green-500" />
        </div>
        <AlertDialog.Title className="mt-5">
          Verify your wallet
        </AlertDialog.Title>
        <AlertDialog.Description className="mt-2">
          To verify your account{" "}
          <span className="rounded-md bg-gray-100 px-1 py-0.5 font-medium font-mono text-gray-900 text-sm dark:bg-gray-700 dark:text-white">
            {accountId}
          </span>
          , please send a one-time transaction.
        </AlertDialog.Description>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <Button
          size="xl"
          fullWidth
          onClick={onConfirm}
          loading={isAddingPubKey}
          data-testid="verify-wallet-confirm"
        >
          {isAddingPubKey ? "Confirming..." : "Confirm account"}
        </Button>
        <Button
          size="xl"
          fullWidth
          variant="secondary"
          onClick={onCancel}
          disabled={isAddingPubKey}
          data-testid="verify-wallet-cancel"
        >
          Cancel
        </Button>
      </div>

      <p className="mt-3 text-center font-medium text-gray-500 text-sm dark:text-gray-400">
        Canceling will sign you out
      </p>
    </>
  )
}
